import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import type { FileRouteTypes } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart, PatientDoc } from '@usacsmideo/types';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { useVideoProgressTracking } from '../../../../hooks/useVideoProgressTracking';
import { useEducationModulesProgress } from '../../../../hooks/useEducationModulesProgress';
import { useStorageDownloadUrl } from '../../../../hooks/useStorageDownloadUrl';
import { ModuleStepper } from '../../../../components/education/ModuleStepper';
import type { ModuleWithProgress } from '../../../../hooks/useEducationModulesProgress';

import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';

export const Route = createFileRoute('/_authenticated/$patientId/education/patientEducation')({
  component: PatientEducationPage,
});

type ActiveModulePlayerProps = {
  patientId: string;
  module: ModuleWithProgress;
  // True when this mount was triggered by a user tap: play even if completed.
  // False for initial-load defaulting: hook's normal "skip completed" rule.
  forcePlay: boolean;
  onFullyWatched: () => void;
};

function ActiveModulePlayer({
  patientId,
  module,
  forcePlay,
  onFullyWatched,
}: ActiveModulePlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { url: videoSource, isLoading: urlLoading, error: urlError } = useStorageDownloadUrl(
    module.storagePath,
  );

  // useVideoProgressTracking flips fullyWatched ~10s before actual end (for the
  // gate signal). We auto-advance on the real `ended` event so the last seconds
  // of the module aren't cut off.
  // On user-tap mounts we trigger play() ourselves below; otherwise let the
  // hook's default (autoPlay && !completed) handle initial load.
  useVideoProgressTracking({
    patientId,
    videoId: module.videoId,
    videoRef,
    autoPlay: !forcePlay,
  });

  // On user-tap mount, override the hook's skip-completed guard and play once
  // the video element has its src loaded. The tap itself is a user gesture, so
  // browser autoplay policy will allow audio to start.
  const forcePlayedRef = useRef(false);
  useEffect(() => {
    if (!forcePlay || forcePlayedRef.current) return;
    if (!videoSource || !videoRef.current) return;
    forcePlayedRef.current = true;
    requestAnimationFrame(() => {
      videoRef.current?.play().catch(() => {
        // Autoplay denied — patient can press the controls.
      });
    });
  }, [forcePlay, videoSource]);

  const firedRef = useRef(false);
  const handleEnded = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    onFullyWatched();
  };

  if (urlError) {
    return (
      <div className="mt-4 text-mideo-darkred text-center px-4 py-6">
        We couldn&apos;t load this video. Please check your connection and try again.
      </div>
    );
  }

  return (
    <div id="general-education-video-container" className="mt-4 flex flex-col items-center">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        id="patient-education-video"
        className="w-10/12"
        playsInline
        controls
        src={videoSource ?? undefined}
        onEnded={handleEnded}
      />
      {urlLoading && <p className="mt-2 text-gray-500">Loading video…</p>}
    </div>
  );
}

function PatientEducationPage() {
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const { modules, allCompleted, currentModuleIndex, isReady } =
    useEducationModulesProgress(patientId);

  const isSelfGuidedGated =
    patientInfo?.navType?.base === 'self' && currentUser?.uid === patientId;

  // wasUserSelected distinguishes initial defaulting (false) from user taps
  // (true). Drives forcePlay behavior in <ActiveModulePlayer>.
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [wasUserSelected, setWasUserSelected] = useState(false);
  useEffect(() => {
    if (isReady && activeIndex === null) {
      setActiveIndex(currentModuleIndex);
    }
  }, [isReady, currentModuleIndex, activeIndex]);

  const selectModule = (i: number) => {
    setActiveIndex(i);
    setWasUserSelected(true);
  };

  const handleModuleFullyWatched = () => {
    if (activeIndex === null) return;
    const nextIncomplete = modules.findIndex((m, i) => i > activeIndex && !m.completed);
    if (nextIncomplete !== -1) {
      // Auto-advance is not a user tap — keep the hook's default autoplay
      // (which plays incomplete next modules, as desired).
      setActiveIndex(nextIncomplete);
      setWasUserSelected(false);
    }
  };

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }

    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }

  const showNextButton = !isSelfGuidedGated || allCompleted;
  const activeModule = activeIndex !== null ? modules[activeIndex] : null;

  return (
    <>
      <div id="general-education-page" className="flex flex-col items-center justify-center">
        <Heading id="general-education-page-header" textContent="Patient Education" />

        {activeIndex !== null && (
          <ModuleStepper
            modules={modules}
            activeIndex={activeIndex}
            isGated={isSelfGuidedGated}
            onSelect={selectModule}
          />
        )}

        {activeModule && (
          <ActiveModulePlayer
            key={activeModule.videoId}
            patientId={patientId}
            module={activeModule}
            forcePlay={wasUserSelected}
            onFullyWatched={handleModuleFullyWatched}
          />
        )}

        {isSelfGuidedGated && !allCompleted && (
          <p className="mt-4 text-xl text-gray-700 text-center">
            Watch all 5 modules to continue.
          </p>
        )}

        {showNextButton && (
          <div id="nav" className="mt-4 flex flex-col items-center">
            <div className="flex flex-col items-center">
              <button
                id="submit-form-info"
                title="Submit Form Info"
                onClick={handleNextButtonClick}
                disabled={false}
                className={classNames.nextButton}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
